import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import DateTimePicker from "components/DateTimePicker";
import FilePreviewModal from "components/FilePreviewModal";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  ASSIGNMENT_APPLICANT_TYPES,
  generateAssignmentPoaPdf,
  getAssignmentPoaContext,
  saveAssignmentPoa,
  type AssignmentPoaContextResponse,
  type AssignmentPoaParty,
  type AssignmentPoaPartyItem,
} from "../../api/assignment";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { toLocalYmd } from "../../utils/filingApiDates";

export const ASSIGNMENT_PREPARATION_OF_POA_FORM_ID =
  "assignment-preparation-of-power-of-attorney-form";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = ASSIGNMENT_APPLICANT_TYPES.find((o) => o.value === value);
  return found?.label ?? value;
}

function FetchedDetailsPanel({
  rows,
  className = "rounded-[8px]",
}: {
  rows: Array<{ label: string; value: string; multiline?: boolean }>;
  className?: string;
}) {
  return (
    <div
      className={`bg-[#F7F7F7] p-4 md:p-5 flex flex-col gap-3 w-full ${className}`}
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {rows.map(({ label, value, multiline }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              multiline ? "whitespace-pre-wrap" : ""
            } ${value ? "text-[#222222]" : "text-[#6D6D6D]"}`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function EditableTextarea({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  invalid = false,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  required?: boolean;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
        {required && <span className="text-[#E7000B] ml-0.5">*</span>}
      </label>
      <div
        className={`box-border w-full bg-white border rounded-[6px] focus-within:ring-1 focus-within:ring-[#222] ${
          invalid ? "border-[#E7000B] focus-within:ring-[#E7000B]" : "border-[#EEEEEE]"
        }`}
      >
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className={`w-full bg-transparent focus:outline-none resize-y px-[10px] py-2 text-[12px] md:text-xs leading-5 ${
            value ? "text-[#222222]" : "text-[#6D6D6D]"
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}

type PartyPdfActionsProps = {
  title: string;
  pdf: AssignmentPoaPartyItem["pdf"];
  pdfIsStale: boolean;
  generating: boolean;
  downloading: boolean;
  previewing: boolean;
  onGenerate: () => void;
  onDownload: () => void;
  onPreview: () => void;
};

function PartyPdfActions({
  title,
  pdf,
  pdfIsStale,
  generating,
  downloading,
  previewing,
  onGenerate,
  onDownload,
  onPreview,
}: PartyPdfActionsProps) {
  return (
    <div className="flex flex-col gap-2.5 w-full">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="text-[12px] md:text-xs font-medium text-[#222222]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {title}
        </span>
        {pdf && pdfIsStale && (
          <span
            className="text-[11px] leading-4 text-[#A86A00] bg-[#FFF8EC] border border-[#FAD9A6] rounded px-1.5 py-0.5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Inputs changed — regenerate to refresh
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating}
          className="flex min-h-[44px] sm:h-9 items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
          {generating ? "Generating..." : pdf ? "Regenerate PDF" : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={onPreview}
          disabled={previewing}
          className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {previewing ? "Generating..." : "Preview PDF"}
        </button>
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading || !pdf}
          className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333333] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
      </div>
    </div>
  );
}

type AssignmentPreparationOfPowerOfAttorneyViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function AssignmentPreparationOfPowerOfAttorneyView({
  leadId,
  onSuccess,
}: AssignmentPreparationOfPowerOfAttorneyViewProps) {
  const [context, setContext] = useState<AssignmentPoaContextResponse | null>(
    null
  );
  const [descriptionOfAssignee, setDescriptionOfAssignee] = useState("");
  const [dateOfPowerOfAttorney, setDateOfPowerOfAttorney] =
    useState<Date | null>(null);

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [partyTab, setPartyTab] = useState<AssignmentPoaParty>("applicant");

  const [generatingParty, setGeneratingParty] =
    useState<AssignmentPoaParty | null>(null);
  const [downloadingParty, setDownloadingParty] =
    useState<AssignmentPoaParty | null>(null);
  const [previewingParty, setPreviewingParty] =
    useState<AssignmentPoaParty | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState<string>("");

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedDescriptionRef = useRef<string>("");
  const lastSavedDateRef = useRef<string>("");

  const isDescriptionMissing = !descriptionOfAssignee.trim();
  const isDateMissing = !dateOfPowerOfAttorney;

  const applyContext = (data: AssignmentPoaContextResponse) => {
    setContext(data);
    const desc = data.assignee.description ?? "";
    setDescriptionOfAssignee(desc);
    lastSavedDescriptionRef.current = desc;
    const dateIso = data.dateOfPowerOfAttorney ?? "";
    setDateOfPowerOfAttorney(dateIso ? new Date(dateIso) : null);
    lastSavedDateRef.current = dateIso;
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAssignmentPoaContext(leadId)
      .then((res) => {
        if (cancelled) return;
        applyContext(res);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Power of Attorney data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
  }, []);

  // Auto-save preview-affecting fields (date + assignee description) — debounced.
  useEffect(() => {
    if (!leadId || loading || !context) return;
    const dateIso = dateOfPowerOfAttorney ? toLocalYmd(dateOfPowerOfAttorney) : "";
    const descChanged = descriptionOfAssignee !== lastSavedDescriptionRef.current;
    const dateChanged = dateIso !== lastSavedDateRef.current;
    if (!descChanged && !dateChanged) return;

    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      const payload: { descriptionOfAssignee?: string; dateOfPowerOfAttorney?: string } = {};
      if (descChanged) payload.descriptionOfAssignee = descriptionOfAssignee;
      if (dateChanged && dateIso) payload.dateOfPowerOfAttorney = dateIso;
      if (Object.keys(payload).length === 0) return;
      saveAssignmentPoa(leadId, payload)
        .then((res) => {
          setContext(res);
          if (descChanged) lastSavedDescriptionRef.current = descriptionOfAssignee;
          if (dateChanged) lastSavedDateRef.current = dateIso;
        })
        .catch(() => {
          // Silent — explicit submit will surface real errors.
        });
    }, 700);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, descriptionOfAssignee, dateOfPowerOfAttorney]);

  const validateBeforeAction = (party: AssignmentPoaParty | "submit"): boolean => {
    if (!context) return false;
    const hasMissing =
      isDateMissing ||
      ((party === "assignee" || party === "submit") && isDescriptionMissing);
    if (hasMissing) {
      setShowValidationErrors(true);
      return false;
    }
    return true;
  };

  const buildPayload = (): { descriptionOfAssignee: string; dateOfPowerOfAttorney?: string } => {
    const payload: { descriptionOfAssignee: string; dateOfPowerOfAttorney?: string } = {
      descriptionOfAssignee,
    };
    if (dateOfPowerOfAttorney) {
      payload.dateOfPowerOfAttorney = toLocalYmd(dateOfPowerOfAttorney);
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving || !context) return;
    if (!validateBeforeAction("submit")) return;
    if (autoSaveDebounceRef.current) {
      clearTimeout(autoSaveDebounceRef.current);
      autoSaveDebounceRef.current = null;
    }
    setSaving(true);
    try {
      const res = await saveAssignmentPoa(leadId, buildPayload());
      applyContext(res);
      toast.success("Power of Attorney saved successfully.");
      setShowValidationErrors(false);
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const persistFieldsBeforePdf = async (party: AssignmentPoaParty): Promise<boolean> => {
    if (!leadId) return false;
    const dateIso = dateOfPowerOfAttorney ? toLocalYmd(dateOfPowerOfAttorney) : "";
    const descChanged = descriptionOfAssignee !== lastSavedDescriptionRef.current;
    const dateChanged = dateIso !== lastSavedDateRef.current;
    // Only the assignee PDF reads descriptionOfAssignee; date affects both.
    const needsDescPersist = party === "assignee" && descChanged;
    if (!needsDescPersist && !dateChanged) return true;
    if (autoSaveDebounceRef.current) {
      clearTimeout(autoSaveDebounceRef.current);
      autoSaveDebounceRef.current = null;
    }
    const payload: { descriptionOfAssignee?: string; dateOfPowerOfAttorney?: string } = {};
    if (needsDescPersist) payload.descriptionOfAssignee = descriptionOfAssignee;
    if (dateChanged && dateIso) payload.dateOfPowerOfAttorney = dateIso;
    try {
      const res = await saveAssignmentPoa(leadId, payload);
      setContext(res);
      if (needsDescPersist) lastSavedDescriptionRef.current = descriptionOfAssignee;
      if (dateChanged) lastSavedDateRef.current = dateIso;
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save before generating PDF"
      );
      return false;
    }
  };

  const handleGeneratePdf = async (party: AssignmentPoaParty) => {
    if (!leadId || !context) return;
    if (!validateBeforeAction(party)) return;
    setGeneratingParty(party);
    try {
      const ok = await persistFieldsBeforePdf(party);
      if (!ok) return;
      await generateAssignmentPoaPdf(leadId, party, true);
      const refreshed = await getAssignmentPoaContext(leadId);
      applyContext(refreshed);
      toast.success(
        `${party === "applicant" ? "Applicant" : "Assignee"} Power of Attorney PDF generated.`
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGeneratingParty(null);
    }
  };

  const handleDownload = async (party: AssignmentPoaParty) => {
    if (!context) return;
    const pdf = context[party].pdf;
    if (!pdf?.url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingParty(party);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename ?? "power-of-attorney.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download PDF");
    } finally {
      setDownloadingParty(null);
    }
  };

  const handlePreview = async (party: AssignmentPoaParty) => {
    if (!leadId || !context) return;
    if (!validateBeforeAction(party)) return;
    const existing = context[party].pdf;
    const isStale = context[party].pdfIsStale;
    const dateIso = dateOfPowerOfAttorney ? toLocalYmd(dateOfPowerOfAttorney) : "";
    const localChanges =
      dateIso !== lastSavedDateRef.current ||
      (party === "assignee" &&
        descriptionOfAssignee !== lastSavedDescriptionRef.current);
    if (existing?.url && !isStale && !localChanges) {
      setPreviewFilename(existing.filename || "power-of-attorney.pdf");
      setPreviewPdfUrl(existing.url);
      return;
    }
    setPreviewingParty(party);
    try {
      const ok = await persistFieldsBeforePdf(party);
      if (!ok) return;
      await generateAssignmentPoaPdf(leadId, party, isStale || localChanges);
      const refreshed = await getAssignmentPoaContext(leadId);
      applyContext(refreshed);
      const pdf = refreshed[party].pdf;
      if (pdf?.url) {
        setPreviewFilename(pdf.filename || "power-of-attorney.pdf");
        setPreviewPdfUrl(pdf.url);
      } else {
        toast.error("Failed to generate PDF for preview.");
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate preview"
      );
    } finally {
      setPreviewingParty(null);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading Power of Attorney data...
        </p>
      </div>
    );
  }

  if (!context) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No Power of Attorney data found. Please complete the previous steps
          first.
        </p>
      </div>
    );
  }

  const { applicant, assignee } = context;

  return (
    <form
      id={ASSIGNMENT_PREPARATION_OF_POA_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Details (fetched from previous step) — tabbed by party */}
      <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
        Details (from previous step)
      </h4>

      <div className="flex flex-col w-full">
        <div className="flex" role="tablist" aria-label="Party details">
          {(
            [
              { id: "applicant", label: "Applicant Details" },
              { id: "assignee", label: "Assignee Details" },
            ] as Array<{ id: AssignmentPoaParty; label: string }>
          ).map((t) => {
            const isActive = partyTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setPartyTab(t.id)}
                className={`px-4 py-2 text-[12px] md:text-xs font-medium leading-5 rounded-t-[8px] transition-colors touch-manipulation ${
                  isActive
                    ? "bg-[#F7F7F7] text-[#222222]"
                    : "bg-transparent text-[#6D6D6D] hover:text-[#222222]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {partyTab === "applicant" ? (
          <FetchedDetailsPanel
            className="rounded-b-[8px] rounded-tr-[8px]"
            rows={[
              { label: "Applicant", value: applicant.name ?? "" },
              { label: "Applicant Address", value: applicant.address ?? "" },
              {
                label: "Applicant Type",
                value: formatApplicantTypeLabel(applicant.type),
              },
              { label: "Signatory's Name", value: applicant.signatoryName ?? "" },
              {
                label: "Signatory's Designation",
                value: applicant.signatoryDesignation ?? "",
              },
              {
                label: "Description of Applicant",
                value: applicant.description ?? "",
                multiline: true,
              },
            ]}
          />
        ) : (
          <FetchedDetailsPanel
            className="rounded-b-[8px] rounded-tr-[8px]"
            rows={[
              { label: "Assignee", value: assignee.name ?? "" },
              { label: "Assignee Address", value: assignee.address ?? "" },
              {
                label: "Assignee Type",
                value: formatApplicantTypeLabel(assignee.type),
              },
              {
                label: "Assignee Signatory's Name",
                value: assignee.signatoryName ?? "",
              },
              {
                label: "Assignee Signatory's Designation",
                value: assignee.signatoryDesignation ?? "",
              },
            ]}
          />
        )}
      </div>

      {/* Editable: Date of Power of Attorney */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <div
          className="flex flex-1 min-w-0 flex-col gap-1 w-full"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Date of Power of Attorney
            <span className="text-[#E7000B] ml-0.5">*</span>
          </label>
          <DateTimePicker
            value={dateOfPowerOfAttorney}
            onChange={setDateOfPowerOfAttorney}
            showTime={false}
            buttonClassName={`min-h-[44px] md:min-h-[36px] h-9 rounded-[6px] text-[12px] md:text-xs ${
              showValidationErrors && isDateMissing
                ? "border-[#E7000B] focus:ring-[#E7000B]"
                : ""
            }`}
          />
        </div>
      </div>

      <EditableTextarea
        label="Description of Assignee"
        value={descriptionOfAssignee}
        onChange={setDescriptionOfAssignee}
        required
        invalid={showValidationErrors && isDescriptionMissing}
      />

      {/* PDF actions — separate per party (backend exposes one PDF per party) */}
      <div className="flex flex-col gap-4 md:gap-5 w-full p-3 md:p-4 rounded-[10px] border border-[#EEEEEE] bg-[#FAFAFA]">
        <PartyPdfActions
          title="Applicant Power of Attorney"
          pdf={applicant.pdf}
          pdfIsStale={applicant.pdfIsStale}
          generating={generatingParty === "applicant"}
          downloading={downloadingParty === "applicant"}
          previewing={previewingParty === "applicant"}
          onGenerate={() => {
            void handleGeneratePdf("applicant");
          }}
          onDownload={() => {
            void handleDownload("applicant");
          }}
          onPreview={() => {
            void handlePreview("applicant");
          }}
        />
        <div className="h-px w-full bg-[#EEEEEE]" />
        <PartyPdfActions
          title="Assignee Power of Attorney"
          pdf={assignee.pdf}
          pdfIsStale={assignee.pdfIsStale}
          generating={generatingParty === "assignee"}
          downloading={downloadingParty === "assignee"}
          previewing={previewingParty === "assignee"}
          onGenerate={() => {
            void handleGeneratePdf("assignee");
          }}
          onDownload={() => {
            void handleDownload("assignee");
          }}
          onPreview={() => {
            void handlePreview("assignee");
          }}
        />
      </div>

      <FilePreviewModal
        open={!!previewPdfUrl}
        onClose={() => setPreviewPdfUrl(null)}
        name={previewFilename || "Power of Attorney"}
        url={previewPdfUrl}
        kind="pdf"
      />
    </form>
  );
}
